/* eslint-disable max-lines */
import { handleActions } from 'redux-actions';
import { Actions, addNewAsset, addNewContainer, addUndoState, clearActiveRegion, clearError, clearUploadAssetProgress, deleteAsset, deleteContainer, editTheme, loadCustomFontsSuccess, loadNewsFailure, loadNewsRequest, loadStocksFailure, loadStocksRequest, loadStocksSuccess, loadWeatherFailure, loadWeatherRequest, loadWeatherSuccess, moveAssetBackward, moveAssetForward, moveContainerBackward, moveContainerForward, previewNewContainer, saveThemeFailure, saveThemeSuccess, setActiveAsset, setActiveRegion, setAssetSize, setEditorThemeProperties, setFitZoom, setZoomLevel, showEditor, updateAsset, updateContainer, uploadAssetFailure, uploadAssetProgress, uploadAssetRequest } from 'actions/editorActions';
import { Actions as ThemeActions, publishThemeFailure, publishThemeRequest } from 'actions/themeActions';
import { Asset, Container, ContainerType, Theme, Stock, Weather, Font, News } from 'shared/models';
import { merge, omit, mapValues, Dictionary } from 'lodash';
import { AnyAction } from 'redux';
import { upgradeThemeCoordinateSystem } from 'logic/upgradeThemeCoordinateSystem';
import { deepSet } from 'logic/deepSet';

const ReduxFormActions = require('redux-form').actionTypes;

type UndoState = {
    theme: Theme | null;
    activeAssetId: string | null;
    activeContainerId: string | null;
};


export type StockProviderId = string;
export type StockSymbol = string;
export type ProviderStockState = Record<StockSymbol, {
    items: Stock[];
    loading: boolean;
    updated: number;
    error: string | null;
}>;


export type State = Readonly<{
    current: UndoState;
    original: Theme | null;
    buildingId: string | null;
    undo: UndoState[];
    redo: UndoState[];
    lastAction: AnyAction | null;
    activeRegion: any; // TODO
    previewRegion: any; // TODO -- perhaps move into the control?
    upload: Dictionary<{
        progress: number;
        error: string | null;
    }>;
    zoom: number;
    fitZoom: number;
    error: string | null;
    errorVisible: boolean;
    publishPending: boolean;
    savePending: boolean;
    fonts: {
        data: Font[];
        loading: boolean;
        error: string | null;
    };
    customFonts: string[];
    news: Dictionary<{
        items: News[];
        loading: boolean;
        updated: number;
        error: string | null;
    }>;
    stocks: Record<StockProviderId, ProviderStockState>;
    weather: Dictionary<{
        items: Weather;
        loading: boolean;
        updated: number;
        error: string | null;
    }>;
    exitPath: string | null;
}>;

export const initialState = Object.freeze<State>({
    current: {
        theme: null,
        activeAssetId: null,
        activeContainerId: null,
    },
    original: null,
    buildingId: null,
    undo: [],
    redo: [],
    lastAction: null,
    activeRegion: null,
    previewRegion: null,
    upload: {},
    zoom: 1,
    fitZoom: 1,
    error: null,
    errorVisible: false,
    publishPending: false,
    savePending: false,
    fonts: {
        data: [],
        loading: false,
        error: null,
    },
    customFonts: [],
    news: {},
    stocks: {},
    weather: {},
    exitPath: null,
});

export const isPIContainerType = (type: ContainerType | string): boolean =>
    //type === 'floorArrival' || // Calvin wants Floor Arrival to be treated separately; and not show up in PI menu
    type === 'maxPersons' || // legacy value
    type === 'maxWeight' || // legacy value
    type === 'elevatorId' || // legacy value
    type === 'piText' ||
    type === 'floor' ||
    type === 'direction'
;

export const isECSContainerType = (type: ContainerType): boolean =>
    type === 'ecs'
;

export const isDMGContainerType = (type: ContainerType): boolean =>
    type === 'dmg'
;

// Helper method to add an array of containers into the given state
// Note: This is only used to add regular containers. All the PI containers
//       are already created ahead of time.
const addContainers = (state: State, newContainers: Container[]) => {
    if (!state.current.theme) {
        return state;
    }
    const allContainers = state.current.theme.containers;
    const allContainerOrder = state.current.theme.containerOrder;

    const piContainerOrder = allContainerOrder.filter(id => !!allContainers[id] && isPIContainerType(allContainers[id].type));
    const ecsContainerOrder = allContainerOrder.filter(id => !!allContainers[id] && isECSContainerType(allContainers[id].type));
    const normalContainerOrder = allContainerOrder.filter(id => !!allContainers[id] && !isPIContainerType(allContainers[id].type) && !isECSContainerType(allContainers[id].type));

    const containerOrder = [
        ...normalContainerOrder,
        ...newContainers.map(c => c.id), // Insert new containers behind the PI/ECS containers (if any)
        ...piContainerOrder,
        ...ecsContainerOrder,
    ];

    const containers = merge({}, state.current.theme.containers, ...newContainers.map(c => ({ [c.id]: c })));

    return {
        ...state,
        current: {
            ...state.current,
            theme: {
                ...state.current.theme,
                containers,
                containerOrder,
            },
        },
    };
};

// Helper method to add an array of assets into the given state
const addAssets = (state: State, assets: Asset[]) =>
    merge({}, state, ...assets.map(a => ({ current: { theme: { assets: { [a.id]: a } } } })))
;

const moveContainer = (state: State, containerId: string, fn: (index: number, containerOrder: string[]) => string[]): State => {
    const { theme } = state.current;
    if (!theme) return state;

    // Find the current index of the given container
    const index = theme.containerOrder.indexOf(containerId);
    if (index < 0) return state;

    // Remove the container from the array and allow fn() to put it back somewhere else
    const containerOrderX = theme.containerOrder.filter(id => id !== containerId);
    return {
        ...state,
        current: {
            ...state.current,
            theme: {
                ...theme,
                containerOrder: fn(index, containerOrderX),
            },
        },
    };
};

const moveAsset = (state: State, containerId: string, assetId: string, fn: (index: number, partialAssetOrder: readonly string[], assetOrder: readonly string[]) => readonly string[]): State => {
    const { theme } = state.current;
    if (!theme) return state;

    const container = theme.containers[containerId];
    if (!container) return state;

    // Find the current index of the given asset
    const index = container.assetOrder.indexOf(assetId);
    if (index < 0) return state;

    // Remove the asset from the array and allow fn() to put it back somewhere else
    const assetOrderX = container.assetOrder.filter(id => id !== assetId);
    return {
        ...state,
        current: {
            ...state.current,
            theme: {
                ...theme,
                containers: {
                    ...theme.containers,
                    [containerId]: {
                        ...container,
                        assetOrder: fn(index, assetOrderX, container.assetOrder),
                    },
                },
            },
        },
    };
};

export const editorReducer = handleActions({
    [Actions.SHOW_EDITOR]: (state: State, action: ReturnType<typeof showEditor>) => {
        const { buildingId, exitPath } = action.payload;
        return {
            ...initialState,
            current: {
                theme: null,
                activeContainerId: null,
                activeAssetId: null,
            },
            original: null,
            buildingId,
            exitPath: exitPath || state.exitPath,
        };
    },
    [Actions.EDIT_THEME]: (state: State, action: ReturnType<typeof editTheme>) => {
        const theme = action.payload;
        const { buildingId } = action.meta;
        return {
            ...state,
            current: {
                theme: upgradeThemeCoordinateSystem(theme),
                activeContainerId: null,
                activeAssetId: null,
            },
            original: theme,
            buildingId,
        };
    },
    [Actions.HIDE_EDITOR]: (state: State, action: any) => {
        return initialState;
    },
    [Actions.SAVE_THEME_REQUEST]: (state: State, action: any) => {
        return { ...state, savePending: true };
    },
    [Actions.SAVE_THEME_SUCCESS]: (state: State, action: ReturnType<typeof saveThemeSuccess>) => {
        const { theme } = action.payload;
        return {
            ...state,
            savePending: false,
            lastAction: null,
            current: {
                ...state.current,
                theme,
            },
            original: theme,
            undo: [],
            redo: [],
        };
    },
    [Actions.SAVE_THEME_FAILURE]: (state: State, action: ReturnType<typeof saveThemeFailure>) => {
        return {
            ...state,
            savePending: false,
            error: action.payload,
            errorVisible: true,
        };
    },
    [Actions.DISMISS_ERROR]: (state: State, action: any) => {
        return { ...state, errorVisible: false };
    },
    [Actions.CLEAR_ERROR]: (state: State, action: ReturnType<typeof clearError>) => {
        return {
            ...state,
            error: null,
            errorVisible: false,
        };
    },
    [Actions.DISMISS_PUBLISH_PENDING]: (state: State, action: any) => {
        return { ...state, publishPending: false };
    },
    [Actions.SET_ACTIVE_CONTAINER]: (state: State, action: any) => {
        const containerId = action.payload;
        const { theme } = state.current;
        if (!theme) return state;
        const isValidContainerId = theme.containers[containerId] !== undefined;
        const activeContainerId = isValidContainerId ? containerId : null;
        const { assetOrder = null } = theme.containers[activeContainerId] || {};
        const activeAssetId = assetOrder && assetOrder.length > 0 ? assetOrder[0] : null;
        return merge({}, state, { current: {
            activeContainerId,
            activeAssetId,
        } });
    },
    [Actions.SET_ACTIVE_ASSET]: (state: State, action: ReturnType<typeof setActiveAsset>) => {
        const activeAssetId = action.payload;
        return merge({}, state, { current: { activeAssetId } });
    },
    [Actions.SET_ZOOM_LEVEL]: (state: State, action: ReturnType<typeof setZoomLevel>) => {
        const zoom = action.payload;
        return { ...state, zoom };
    },
    [Actions.SET_FIT_ZOOM]: (state: State, action: ReturnType<typeof setFitZoom>) => {
        // Fit Zoom is the calculated zoom level that shows the canvas
        // as large as possible while maintaining a 20px margin
        const fitZoom = action.payload;
        return { ...state, fitZoom };
    },
    [Actions.SET_ACTIVE_REGION]: (state: State, action: ReturnType<typeof setActiveRegion>) => {
        const activeRegion = action.payload;
        return { ...state, activeRegion };
    },
    [Actions.CLEAR_ACTIVE_REGION]: (state: State, action: ReturnType<typeof clearActiveRegion>) => {
        return { ...state, activeRegion: null };
    },
    [Actions.SET_EDITOR_THEME_PROPERTIES]: (state: State, action: ReturnType<typeof setEditorThemeProperties>) => {
        const { displayName, width, height, requirements, options } = action.payload;

        const { theme } = state.current;
        if (!theme) return state;

        const newState = merge({}, state, {
            current: {
                theme: {
                    displayName,
                    height,
                    width,
                    //requirements, // This fails in merge because we use undefined for false (and so this merge can't overwrite a true value)
                },
            },
        });
        // Manually overwrite the requirements to clear any deleted flags
        newState.current.theme.requirements = requirements;
        newState.current.theme.options = options;
        return newState;
    },
    [Actions.PREVIEW_NEW_CONTAINER]: (state: State, action: ReturnType<typeof previewNewContainer>) => {
        const previewRegion = action.payload;
        return merge({}, state, {
            previewRegion,
            current: {
                activeContainerId: null,
                activeAssetId: null,
            },
        });
    },
    [Actions.ADD_NEW_CONTAINER]: (state: State, action: ReturnType<typeof addNewContainer>) => {
        const { container, assets } = action.payload;
        return merge(
            addAssets(state, assets),
            addContainers(state, [ container ]),
            {
                current: {
                    activeContainerId: container.id,
                    activeAssetId: (assets && assets[0] && assets[0].id) || null,
                },
                activeRegion: null,
                previewRegion: null,
            },
        );
    },
    [Actions.UPDATE_CONTAINER]: (state: State, action: ReturnType<typeof updateContainer>) => {
        const container = action.payload;
        return merge({}, state, { current: { theme: { containers: { [container.id]: container } } } });
    },
    [Actions.DELETE_CONTAINER]: (state: State, action: ReturnType<typeof deleteContainer>) => {
        const activeContainerId = action.payload;
        const { theme } = state.current;
        if (!theme) return state;
        const assetsToDelete = theme.containers[activeContainerId].assetOrder;
        const assets = omit(theme.assets, assetsToDelete);
        const containers = omit(theme.containers, activeContainerId);
        const containerOrder = theme.containerOrder.filter(id => id !== activeContainerId);
        return {
            ...state,
            current: {
                ...state.current,
                activeAssetId: null,
                activeContainerId: null,
                theme: {
                    ...theme,
                    assets,
                    containers,
                    containerOrder,
                },
            },
        };
    },
    [Actions.MOVE_CONTAINER_FORWARD]: (state: State, action: ReturnType<typeof moveContainerForward>) => {
        const containerId = action.payload;
        return moveContainer(state, containerId, (index, partialContainerOrder) => {
            if (index <= 0) return state.current.theme!.containerOrder;
            return [
                ...partialContainerOrder.slice(0, index + 1),
                containerId,
                ...partialContainerOrder.slice(index + 1),
            ];
        });
    },
    [Actions.MOVE_CONTAINER_BACKWARD]: (state: State, action: ReturnType<typeof moveContainerBackward>) => {
        const containerId = action.payload;
        return moveContainer(state, containerId, (index, partialContainerOrder) => {
            if (index < 2) return state.current.theme!.containerOrder; // 2 because the background layer has to be at the bottom
            return [
                ...partialContainerOrder.slice(0, index - 1),
                containerId,
                ...partialContainerOrder.slice(index - 1),
            ];
        });
    },
    [Actions.SET_ASSET_SIZE]: (state: State, action: ReturnType<typeof setAssetSize>) => {
        const { assetId, width, height } = action.payload;
        return merge({}, state, { current: { theme: { assets: { [assetId]: { properties: {
            width,
            height,
        } } } } } });
    },
    [Actions.UPLOAD_ASSET_REQUEST]: (state: State, action: ReturnType<typeof uploadAssetRequest>) => {
        const { assetId, width, height } = action.payload;
        // Note: we also know the filename here... but the UI uses the presence of
        //       the filename to indicate that the file has finished uploading to S3
        return merge({}, state,
            { upload: { [assetId]: { progress: 0, error: null } } },
            { current: { theme: { assets: { [assetId]: { properties: { width, height } } } } } },
        );
    },
    [Actions.UPLOAD_ASSET_PROGRESS]: (state: State, action: ReturnType<typeof uploadAssetProgress>) => {
        const { assetId, progress } = action.payload;
        return merge({}, state, { upload: { [assetId]: { progress } } });
    },
    [Actions.UPLOAD_ASSET_SUCCESS]: (state: State, action: any) => {
        const { assetId, filename } = action.payload;
        const { theme } = state.current;
        if (!theme) return state;
        return {
            ...state,
            upload: omit(state.upload, assetId),
            current: {
                ...state.current,
                theme: {
                    ...theme,
                    assets: merge({}, theme.assets, { [assetId]: { properties: { filename } } }),
                },
            },
        };
    },
    [Actions.UPLOAD_ASSET_FAILURE]: (state: State, action: ReturnType<typeof uploadAssetFailure>) => {
        const error = action.payload;
        const { assetId } = action.meta;
        return merge({}, state,
            { upload: { [assetId]: { error } } },
            { error, errorVisible: true }
        );
    },
    [Actions.CLEAR_UPLOAD_ASSET_PROGRESS]: (state: State, action: ReturnType<typeof clearUploadAssetProgress>) => {
        const assetId = action.payload;
        return {
            ...state,
            upload: omit(state.upload, assetId),
        };
    },
    [Actions.ADD_NEW_ASSET]: (state: State, action: ReturnType<typeof addNewAsset>) => {
        const { asset, containerId } = action.payload;
        const { theme } = state.current;
        if (!theme) return state;
        const container = theme.containers[containerId];
        if (!container) return state;
        return {
            ...state,
            current: {
                ...state.current,
                activeContainerId: containerId,
                activeAssetId: asset.id,
                theme: {
                    ...theme,
                    assets: {
                        ...theme.assets,
                        [asset.id]: asset,
                    },
                    containers: {
                        ...theme.containers,
                        [containerId]: {
                            ...container,
                            assetOrder: container.assetOrder.concat(asset.id),
                        },
                    },
                },
            },
        };
    },
    [Actions.UPDATE_ASSET]: (state: State, action: ReturnType<typeof updateAsset>) => {
        const asset = action.payload;
        if (!state.current?.theme?.assets[asset.id]) return state;
        return deepSet(state, `current.theme.assets.${asset.id}`, asset);
    },
    [Actions.DELETE_ASSET]: (state: State, action: ReturnType<typeof deleteAsset>) => {
        const assetId = action.payload;
        // TODO: move to delete list if asset has physical media that needs to be deleted
        // TODO: should we distinguish between removing an asset from a container and outright deleting the asset?
        const { activeContainerId, theme } = state.current;
        if (!theme || !activeContainerId) return state;
        const { assetOrder } = theme.containers[activeContainerId];
        const indexOfActiveAsset = assetOrder.indexOf(assetId);
        const indexOfNextActiveAsset = Math.min(indexOfActiveAsset + 1, assetOrder.length - 1);
        const nextActiveAssetId = indexOfNextActiveAsset >= 0 ? assetOrder[indexOfNextActiveAsset] : null;
        return {
            ...state,
            upload: omit(state.upload, assetId),
            current: {
                ...state.current,
                activeAssetId: state.current.activeAssetId === assetId ? nextActiveAssetId : state.current.activeAssetId,
                theme: {
                    ...theme,
                    assets: omit(theme.assets, assetId),
                    containers: mapValues(theme.containers, c => {
                        const { assetOrder } = c;
                        if (assetOrder.indexOf(assetId) >= 0) {
                            return {
                                ...c,
                                assetOrder: assetOrder.filter(id => id !== assetId),
                            };
                        }
                        return c;
                    }),
                },
            },
        };
    },
    [Actions.MOVE_ASSET_FORWARD]: (state: State, action: ReturnType<typeof moveAssetForward>) => {
        const { assetId, containerId } = action.payload;
        return moveAsset(state, containerId, assetId, (index, partialAssetOrder, assetOrder) => {
            if (index <= 0) return assetOrder;
            return [
                ...partialAssetOrder.slice(0, index - 1),
                assetId,
                ...partialAssetOrder.slice(index - 1),
            ];
        });
    },
    [Actions.MOVE_ASSET_BACKWARD]: (state: State, action: ReturnType<typeof moveAssetBackward>) => {
        const { assetId, containerId } = action.payload;
        return moveAsset(state, containerId, assetId, (index, partialAssetOrder, assetOrder) => {
            if (index === -1 || index >= partialAssetOrder.length) return assetOrder;
            return [
                ...partialAssetOrder.slice(0, index + 1),
                assetId,
                ...partialAssetOrder.slice(index + 1),
            ];
        });
    },

    [Actions.ADD_UNDO_STATE]: (state: State, action: ReturnType<typeof addUndoState>) => {
        const currentState = state.current;
        const currentAction = action.payload;
        const lastAction = state.lastAction;
        let undoList = state.undo;

        if (lastAction && lastAction.meta && lastAction.meta.mergeForward) {
            // Throw away the previous state (i.e. effectively replace the previous state with the current state)
            undoList = undoList.slice(0, undoList.length - 1);
        }
        return {
            ...state,
            undo: undoList.concat(currentState),
            lastAction: currentAction,
            redo: [],
        };
    },
    [Actions.UNDO_STATE]: (state: State, action: any) => {
        const { undo, redo } = state;
        const undoState = undo[undo.length - 1];
        return {
            ...state,
            current: undoState,
            undo: undo.slice(0, undo.length - 1),
            lastAction: null,
            redo: redo.concat(state.current),
        };
    },
    [Actions.REDO_STATE]: (state: State, action: any) => {
        const { undo, redo } = state;
        const redoState = redo[redo.length - 1];
        return {
            ...state,
            current: redoState,
            undo: undo.concat(state.current),
            lastAction: null,
            redo: redo.slice(0, redo.length - 1),
        };
    },

    [Actions.LOAD_FONTS_SUCCESS]: (state: State, action: any) => {
        const fonts = action.payload;
        return {
            ...state,
            fonts: {
                ...state.fonts,
                data: fonts,
                loading: false,
                error: null,
            },
        };
    },

    [Actions.LOAD_CUSTOM_FONTS_SUCCESS]: (state: State, action: ReturnType<typeof loadCustomFontsSuccess>) => {
        const customFonts = action.payload;
        return {
            ...state,
            customFonts,
        };
    },

    [Actions.LOAD_NEWS_REQUEST]: (state: State, action: ReturnType<typeof loadNewsRequest>) => {
        const channelId = action.payload;
        return merge({}, state, { news: { [channelId]: { loading: true } } });
    },
    [Actions.LOAD_NEWS_SUCCESS]: (state: State, action: any) => {
        const news = action.payload;
        const { channelId, updated } = action.meta;
        return {
            ...state,
            news: {
                ...state.news,
                [channelId]: {
                    items: news,
                    loading: false,
                    updated,
                    error: null,
                },
            },
        };
    },
    [Actions.LOAD_NEWS_FAILURE]: (state: State, action: ReturnType<typeof loadNewsFailure>) => {
        const { channelId } = action.meta;
        return {
            ...state,
            news: {
                ...state.news,
                [channelId]: {
                    items: [],
                    error: action.payload,
                    loading: false,
                    updated: 0,
                },
            },
        };
    },

    [Actions.LOAD_WEATHER_REQUEST]: (state: State, action: ReturnType<typeof loadWeatherRequest>) => {
        const { provider, locationOrLatLong } = action.payload;
        return {
            ...state,
            weather: {
                ...state.weather,
                [`${provider}:${locationOrLatLong}`]: {
                    loading: true,
                },
            },
        };
    },
    [Actions.LOAD_WEATHER_SUCCESS]: (state: State, action: ReturnType<typeof loadWeatherSuccess>) => {
        const weather = action.payload;
        const { provider, locationOrLatLong, updated } = action.meta;
        return {
            ...state,
            weather: {
                ...state.weather,
                [`${provider}:${locationOrLatLong}`]: {
                    items: weather,
                    loading: false,
                    updated,
                    error: null,
                },
            },
        };
    },
    [Actions.LOAD_WEATHER_FAILURE]: (state: State, action: ReturnType<typeof loadWeatherFailure>) => {
        const { provider, locationOrLatLong, updated } = action.meta;
        return merge({}, state, {
            weather: {
                [`${provider}:${locationOrLatLong}`]: {
                    loading: false,
                    updated,
                    error: action.payload,
                },
            },
        });
    },

    [Actions.LOAD_STOCKS_REQUEST]: (state: State, action: ReturnType<typeof loadStocksRequest>) => {
        const { provider, symbols } = action.payload;
        return merge({}, state, { stocks: { [provider]: { [symbols]: { loading: true } } } });
    },
    [Actions.LOAD_STOCKS_SUCCESS]: (state: State, action: ReturnType<typeof loadStocksSuccess>) => {
        const stocks = action.payload;
        const { provider, symbols, updated } = action.meta;
        return merge({}, state, { stocks: { [provider]: { [symbols]: {
            items: stocks,
            loading: false,
            updated,
            error: null,
        } } } });
    },
    [Actions.LOAD_STOCKS_FAILURE]: (state: State, action: ReturnType<typeof loadStocksFailure>) => {
        const { provider, symbols } = action.meta;
        return merge({}, state, { stocks: { [provider]: { [symbols]: {
            error: action.payload,
            loading: false,
        } } } });
    },


    //
    //  Theme Actions
    //
    [ThemeActions.PUBLISH_THEME_REQUEST]: (state: State, action: ReturnType<typeof publishThemeRequest>) => {
        return { ...state, publishPending: true };
    },
    //[ThemeActions.PUBLISH_THEME_SUCCESS]: (state: State, action: any) => {
    //  Don't reset the flag here because this event just means the async process was initiated
    //},
    [ThemeActions.PUBLISH_THEME_FAILURE]: (state: State, action: ReturnType<typeof publishThemeFailure>) => {
        return {
            ...state,
            errorVisible: true,
            publishPending: false,
        };
    },


    //
    //  Form Actions (legacy redux-forms forms)
    //
    [ReduxFormActions.CHANGE]: (state: State, action: any) => {
        const { form } = action.meta;
        let { field } = action.meta;
        const value = action.payload;
        const { activeAssetId, activeContainerId } = state.current;
        const fieldPath = [];
        while (field.indexOf('.') > 0) {
            const parent = field.substr(0, field.indexOf('.'));
            fieldPath.push(parent);
            field = field.substr(parent.length + 1);
        }

        let nested = { [field]: value };
        for (let i = fieldPath.length - 1; i >= 0; i--) {
            nested = { [fieldPath[i]]: nested };
        }

        if (form.indexOf('-asset-') > 0 && activeAssetId) {
            return merge({}, state, { current: { theme: { assets: { [activeAssetId]: { properties: nested } } } } });
        }
        else if (form === 'container-properties' && activeContainerId) {
            // Are we updating the background container?
            const { containerOrder } = state.current.theme!;
            const backgroundContainerId = containerOrder.length > 0 ? containerOrder[0] : null;
            const isBackgroundContainer = activeContainerId === backgroundContainerId;
            if (isBackgroundContainer && (field === 'width' || field === 'height')) {
                // Resize the theme when the user resizes the background container
                state = merge({}, state, { current: { theme: { [field]: parseInt(value, 10) } } });
            }

            return merge({}, state, { current: { theme: { containers: { [activeContainerId]: nested } } } });
        }
        return state;
    },
} as any, initialState);
